import { useEffect, useState } from 'react';
import { Card, Button, Spinner, FormField } from '../components/ui';

const NOTE_TYPES = [
  { value: 'inspection',    label: 'Inspection',    icon: '🔍', color: '#3b82f6' },
  { value: 'maintenance',   label: 'Maintenance',   icon: '🔧', color: '#f59e0b' },
  { value: 'incident',      label: 'Incident',      icon: '⚠️', color: '#ef4444' },
  { value: 'communication', label: 'Communication', icon: '💬', color: '#8b5cf6' },
  { value: 'legal',         label: 'Legal',         icon: '⚖️', color: '#059669' },
  { value: 'general',       label: 'General',       icon: '📝', color: '#6b7280' },
];

function getNoteType(value) {
  return NOTE_TYPES.find(t => t.value === value) || NOTE_TYPES[NOTE_TYPES.length - 1];
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' });
}

function groupByMonth(notes) {
  const groups = {};
  notes.forEach(n => {
    const d = new Date(n.note_date);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-GB', { month:'long', year:'numeric' });
    if (!groups[key]) groups[key] = { label, notes: [] };
    groups[key].notes.push(n);
  });
  return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
}

const EMPTY_FORM = { note_type:'general', note_date:'', title:'', body:'' };

export default function Notes() {
  const [notes, setNotes]         = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [editID, setEditID]       = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [filterProp, setFilterProp] = useState('');
  const [filterType, setFilterType] = useState('');
  const [selectedProp, setSelectedProp] = useState('');
  const [search, setSearch]       = useState('');

  const token = () => localStorage.getItem('token');
  const headers = () => { const ac = localStorage.getItem('lc_access_code'); return { 'Content-Type':'application/json', Authorization:`Bearer ${token()}`, ...(ac ? {'X-Access-Code': ac} : {}) }; };

  async function loadNotes(propId = filterProp) {
    const url = propId ? `/api/property-notes?property_id=${propId}` : '/api/property-notes';
    const d = await fetch(url, { headers: headers() }).then(r => r.json());
    setNotes(Array.isArray(d) ? d : []);
  }

  useEffect(() => {
    Promise.all([
      fetch('/api/property-notes', { headers: headers() }).then(r => r.json()),
      fetch('/api/properties',     { headers: headers() }).then(r => r.json()),
    ]).then(([n, p]) => {
      setNotes(Array.isArray(n) ? n : []);
      setProperties(Array.isArray(p) ? p : []);
      setLoading(false);
    });
  }, []);

  useEffect(() => { loadNotes(filterProp); }, [filterProp]);

  function openNew() {
    setForm({ ...EMPTY_FORM, note_date: new Date().toISOString().split('T')[0] });
    setSelectedProp(filterProp || '');
    setEditID(null);
    setShowForm(true);
  }

  function openEdit(n) {
    setForm({ note_type: n.note_type, note_date: n.note_date, title: n.title, body: n.body || '' });
    setSelectedProp(n.property_id);
    setEditID(n.id);
    setShowForm(true);
  }

  async function handleSave() {
    if (!selectedProp) return;
    setSaving(true);
    try {
      const body = { ...form, body: form.body || null };
      if (editID) {
        await fetch(`/api/property-notes/${editID}`, { method:'PUT', headers: headers(), body: JSON.stringify(body) });
      } else {
        await fetch(`/api/properties/${selectedProp}/notes`, { method:'POST', headers: headers(), body: JSON.stringify(body) });
      }
      await loadNotes();
      setShowForm(false);
    } finally { setSaving(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this note?')) return;
    await fetch(`/api/property-notes/${id}`, { method:'DELETE', headers: headers() });
    setNotes(n => n.filter(x => x.id !== id));
  }

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  // Filter + search
  let filtered = notes;
  if (filterType) filtered = filtered.filter(n => n.note_type === filterType);
  if (search)     filtered = filtered.filter(n =>
    n.title.toLowerCase().includes(search.toLowerCase()) ||
    (n.body || '').toLowerCase().includes(search.toLowerCase())
  );

  const grouped = groupByMonth(filtered);

  if (loading) return <Spinner />;

  return (
    <div className="fade-up">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:32, flexWrap:'wrap', gap:16 }}>
        <div>
          <h1 style={{ fontSize:32, fontWeight:300 }}>Notes & <em>Timeline</em></h1>
          <p style={{ color:'var(--ink-3)', marginTop:4 }}>Log inspections, incidents, communications and key events per property</p>
        </div>
        <Button onClick={openNew}>+ Add note</Button>
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:12, marginBottom:24, flexWrap:'wrap' }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search notes…"
          style={{ padding:'8px 14px', borderRadius:8, border:'1.5px solid var(--border)', background:'var(--bg)', fontSize:14, minWidth:200, color:'var(--ink)' }}
        />
        {properties.length > 1 && (
          <select value={filterProp} onChange={e => setFilterProp(e.target.value)}
            style={{ padding:'8px 12px', borderRadius:8, border:'1.5px solid var(--border)', background:'var(--bg)', fontSize:14 }}>
            <option value="">All properties</option>
            {properties.map(p => <option key={p.id} value={p.id}>{p.address_line1}</option>)}
          </select>
        )}
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          style={{ padding:'8px 12px', borderRadius:8, border:'1.5px solid var(--border)', background:'var(--bg)', fontSize:14 }}>
          <option value="">All types</option>
          {NOTE_TYPES.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
        </select>
      </div>

      {/* Timeline */}
      {grouped.length === 0 ? (
        <Card style={{ textAlign:'center', padding:48 }}>
          <p style={{ fontSize:32, marginBottom:12 }}>📝</p>
          <p style={{ fontWeight:600, marginBottom:8 }}>No notes yet</p>
          <p style={{ color:'var(--ink-3)', fontSize:14, marginBottom:20 }}>
            Log inspections, maintenance visits, tenant communications and key events. Build a complete property history.
          </p>
          <Button onClick={openNew}>+ Add your first note</Button>
        </Card>
      ) : (
        <div>
          {grouped.map(([key, group]) => (
            <div key={key} style={{ marginBottom:32 }}>
              <p style={{ fontSize:13, fontWeight:600, color:'var(--ink-3)', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:12 }}>
                {group.label}
              </p>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {group.notes.map(n => {
                  const nt = getNoteType(n.note_type);
                  return (
                    <Card key={n.id} style={{ padding:'16px 20px' }}>
                      <div style={{ display:'flex', gap:14, alignItems:'flex-start' }}>
                        {/* Type indicator */}
                        <div style={{
                          width:36, height:36, borderRadius:'50%', flexShrink:0,
                          background: `${nt.color}20`,
                          display:'flex', alignItems:'center', justifyContent:'center',
                          fontSize:16,
                        }}>
                          {nt.icon}
                        </div>

                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:8 }}>
                            <div>
                              <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                                <p style={{ fontWeight:600, fontSize:15 }}>{n.title}</p>
                                <span style={{ fontSize:11, fontWeight:600, color:nt.color, background:`${nt.color}15`, padding:'2px 8px', borderRadius:20 }}>
                                  {nt.label}
                                </span>
                              </div>
                              <div style={{ display:'flex', gap:12, marginTop:2 }}>
                                <span style={{ fontSize:12, color:'var(--ink-3)' }}>📅 {formatDate(n.note_date)}</span>
                                {n.property_address && !filterProp && (
                                  <span style={{ fontSize:12, color:'var(--ink-3)' }}>🏘 {n.property_address}</span>
                                )}
                              </div>
                            </div>
                            <div style={{ display:'flex', gap:6 }}>
                              <Button size="sm" variant="ghost" onClick={() => openEdit(n)}>Edit</Button>
                              <Button size="sm" variant="ghost" onClick={() => handleDelete(n.id)} style={{ color:'var(--danger)' }}>Delete</Button>
                            </div>
                          </div>
                          {n.body && (
                            <p style={{ fontSize:14, color:'var(--ink-2)', marginTop:8, lineHeight:1.5, whiteSpace:'pre-wrap' }}>
                              {n.body}
                            </p>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form modal */}
      {showForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:9999, backdropFilter:'blur(2px)', display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'40px 20px', overflowY:'auto' }}
          onClick={e => e.target === e.currentTarget && setShowForm(false)}>
          <div style={{ background:'#fff', borderRadius:16, padding:32, width:'100%', maxWidth:520, boxShadow:'0 20px 60px rgba(0,0,0,.3)' }}>
            <h3 style={{ fontSize:20, marginBottom:20 }}>{editID ? 'Edit note' : 'Add note'}</h3>

            {!editID && (
              <FormField label="Property *">
                <select value={selectedProp} onChange={e => setSelectedProp(e.target.value)}>
                  <option value="">Select property…</option>
                  {properties.map(p => <option key={p.id} value={p.id}>{p.address_line1}</option>)}
                </select>
              </FormField>
            )}

            <FormField label="Type">
              <select value={form.note_type} onChange={set('note_type')}>
                {NOTE_TYPES.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
              </select>
            </FormField>

            <FormField label="Date *">
              <input type="date" value={form.note_date} onChange={set('note_date')} />
            </FormField>

            <FormField label="Title *">
              <input value={form.title} onChange={set('title')} placeholder="e.g. Annual gas safety inspection" />
            </FormField>

            <FormField label="Details">
              <textarea value={form.body} onChange={set('body')} rows={4}
                placeholder="Add more detail — what was found, what action was taken, who was present…" />
            </FormField>

            <div style={{ display:'flex', gap:12, justifyContent:'flex-end', marginTop:8 }}>
              <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button loading={saving} onClick={handleSave} disabled={!selectedProp || !form.title || !form.note_date}>
                {editID ? 'Save changes' : 'Add note'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}